/* eslint-disable max-lines */
import { formatShortDateWithWeekday, formatTime } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, DialogContent, Stack } from "@mui/material";
import { FullScreenDialog } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import type { ExternalAccountDetailsAttributes } from "@src/appV2/redesign/Accounts/PayoutSettings/api/schema";
import { ExternalAccountLogo } from "@src/appV2/redesign/Accounts/PayoutSettings/components/ExternalAccountLogo";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { SelectableBox } from "@src/appV2/redesign/components/SelectableBox";
import { getExpectedPayoutDate } from "@src/appV2/redesign/Payments/utils/getExpectedPayoutDate";
import {
  CbhIcon,
  Divider,
  DotSeparatedList,
  Pill,
  PromotionBanner,
  Text,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import Confetti from "react-confetti";

import { DialogFooter } from "../../../components/DialogFooter";

interface EarningsSuccessBottomSheetProps {
  modalState: UseModalState;
  isAfterAnotherShift: boolean;
  grossEarnings?: number;
  hourlyRate?: number;
  isPayoutInstant: boolean;
  payoutDestination: ExternalAccountDetailsAttributes | undefined;
  shiftId: string;
  payoutFee: number;
  ten99PolicyFee?: number;
  pendingApprovalAmountInDollars?: number;
  isMissedPunchRequestEnabledV2?: boolean;
  onOpenPayoutSettings: () => void;
  onContinue: () => void;
  continueButtonIsLoading?: boolean;
}

export function EarningsSuccessBottomSheet(props: EarningsSuccessBottomSheetProps) {
  const {
    modalState,
    grossEarnings,
    hourlyRate,
    payoutFee,
    ten99PolicyFee,
    pendingApprovalAmountInDollars,
    isMissedPunchRequestEnabledV2 = false,
    isPayoutInstant,
    payoutDestination,
    shiftId,
    isAfterAnotherShift,
    onOpenPayoutSettings,
    onContinue,
    continueButtonIsLoading = false,
  } = props;

  const effectivePayoutFee =
    isDefined(grossEarnings) && grossEarnings > payoutFee && !isAfterAnotherShift ? payoutFee : 0;
  const netEarnings = isDefined(grossEarnings)
    ? Math.max(0, grossEarnings - effectivePayoutFee - (ten99PolicyFee ?? 0))
    : 0;
  const displayHourlyRate = !isDefined(grossEarnings) && isDefined(hourlyRate);
  const expectedPayoutDate = getExpectedPayoutDate({
    paymentDate: new Date(),
    isPayoutInstant,
  });

  useLogEffect(
    APP_V2_USER_EVENTS.EARNINGS_BOTTOM_SHEET_VIEWED,
    {
      shiftId,
      grossEarnings,
      netEarnings: isDefined(grossEarnings) ? netEarnings : undefined,
      payoutFee,
      ten99PolicyFee,
      hourlyRate: displayHourlyRate ? hourlyRate : undefined,
      isPayoutInstant,
      payoutDestination,
    },
    { enabled: modalState.modalIsOpen }
  );

  const getPayoutAmount = (): string => {
    if (displayHourlyRate) {
      return formatDollarsAsUsd(hourlyRate);
    }

    if (isDefined(grossEarnings)) {
      return formatDollarsAsUsd(netEarnings);
    }

    return "N/A";
  };

  return (
    <FullScreenDialog modalState={modalState} variant="tertiary">
      <DialogContent sx={{ padding: 0 }}>
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          numberOfPieces={400}
          gravity={0.8}
          recycle={false}
        />
        <Stack spacing={6} sx={{ paddingX: 5, paddingY: 6 }}>
          <Stack
            direction="column"
            gap={6}
            sx={{
              background: (theme) =>
                // eslint-disable-next-line cbh-rules/no-inline-gradient -- TODO: migrate to theme palette gradient
                `linear-gradient(rgba(218, 241, 232, 1), ${theme.palette.background.primary})`,
              borderRadius: 4,
              paddingX: 6,
              paddingY: 7,
              textAlign: "center",
            }}
          >
            <Stack spacing={4} alignItems="center">
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  // eslint-disable-next-line cbh-rules/no-inline-gradient -- TODO: migrate to theme palette gradient
                  background: "linear-gradient(rgba(173, 243, 199, 1), rgba(218, 241, 232, 1))",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    backgroundColor: (theme) => theme.palette.common.white,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  <CbhIcon
                    type="bolt"
                    size="xLarge"
                    sx={{
                      color: (theme) => theme.palette.success.dark,
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: -12,
                      left: -22,
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor: (theme) => theme.palette.intent?.success?.text ?? "",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CbhIcon
                      type="coin"
                      size="xLarge"
                      sx={{
                        color: (theme) => theme.palette.background?.tertiary ?? "",
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              <Stack spacing={2} alignItems="center">
                <Text
                  semibold
                  variant="h5"
                  sx={{
                    textAlign: "center",
                    color: (theme) => theme.palette.intent?.success?.text,
                  }}
                >
                  Great work! You earned
                </Text>
                <Text
                  semibold
                  variant="h3"
                  sx={{
                    textAlign: "center",
                    color: (theme) => theme.palette.intent?.success?.text ?? "",
                  }}
                >
                  {getPayoutAmount()}
                </Text>
                {displayHourlyRate && (
                  <Text
                    semibold
                    variant="h5"
                    sx={{
                      textAlign: "center",
                      color: (theme) => theme.palette.intent?.success?.text,
                    }}
                  >
                    per hour
                  </Text>
                )}
              </Stack>
            </Stack>

            <Stack alignItems="center">
              <Pill
                label={
                  <Stack direction="row" gap={1} alignItems="center">
                    {isPayoutInstant ? (
                      <>
                        <CbhIcon type="bolt-filled" size="xSmall" />
                        <Text inline semibold variant="body2">
                          <DotSeparatedList>
                            {["InstantPay", "Expected in 30 minutes"]}
                          </DotSeparatedList>
                        </Text>
                      </>
                    ) : (
                      <>
                        <CbhIcon type="clock" size="xSmall" />
                        <Text semibold variant="body2">
                          Expected within 3 business days
                        </Text>
                      </>
                    )}
                  </Stack>
                }
                sx={{
                  backgroundColor: (theme) => `${theme.palette.background?.tertiary ?? ""}`,
                  color: (theme) => theme.palette.text?.primary ?? "",
                }}
              />
            </Stack>
          </Stack>

          {isDefined(grossEarnings) && (
            <Stack
              gap={2}
              sx={{
                paddingTop: 2,
              }}
            >
              {isDefined(payoutDestination) && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={(theme) => ({ height: theme.spacing(8) })}
                >
                  <Text variant="body1" sx={{ color: (theme) => theme.palette.text.tertiary }}>
                    Payout destination
                  </Text>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <ExternalAccountLogo externalAccount={payoutDestination} iconSize="medium" />
                    <Text semibold variant="body1">
                      {payoutDestination.providerName} ···· {payoutDestination.last4}
                    </Text>
                  </Stack>
                </Stack>
              )}

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={(theme) => ({ height: theme.spacing(8) })}
              >
                <Text variant="body1" sx={{ color: (theme) => theme.palette.text.tertiary }}>
                  Expected by
                </Text>
                <Text semibold variant="body1">
                  {isPayoutInstant
                    ? formatTime(expectedPayoutDate)
                    : formatShortDateWithWeekday(expectedPayoutDate)}
                </Text>
              </Stack>
              {!isPayoutInstant && (
                <SelectableBox
                  endIconType="chevron-right"
                  startIconType="bolt-filled"
                  variant="secondary"
                  sx={{ textAlign: "left" }}
                  title="Go to Payout Settings to enable InstantPay"
                  onToggleSelect={() => {
                    onOpenPayoutSettings();
                    logEvent(
                      APP_V2_USER_EVENTS.EARNINGS_BOTTOM_SHEET_INSTANT_PAYOUT_SETTINGS_CLICKED,
                      {
                        shiftId,
                        grossEarnings,
                        netEarnings,
                        isPayoutInstant,
                        payoutDestination,
                        payoutFee,
                        hourlyRate,
                      }
                    );
                  }}
                >
                  <Stack direction="column" gap={1} marginLeft={1}>
                    <Text bold variant="body2">
                      Want faster pay on your next shift?
                    </Text>
                    <Text variant="body2">
                      Switch to InstantPay and get paid right after your shifts end.
                    </Text>
                  </Stack>
                </SelectableBox>
              )}

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={(theme) => ({ height: theme.spacing(8) })}
              >
                <Text variant="body1" sx={{ color: (theme) => theme.palette.text.tertiary }}>
                  Gross earnings
                </Text>
                <Text semibold variant="body1">
                  {formatDollarsAsUsd(grossEarnings)}
                </Text>
              </Stack>

              {isDefined(ten99PolicyFee) && ten99PolicyFee > 0 && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={(theme) => ({ height: theme.spacing(8) })}
                >
                  <Text variant="body1" sx={{ color: (theme) => theme.palette.text.tertiary }}>
                    Workers&apos; compensation coverage fee
                  </Text>
                  <Text semibold variant="body1">
                    -{formatDollarsAsUsd(ten99PolicyFee)}
                  </Text>
                </Stack>
              )}

              {effectivePayoutFee > 0 && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={(theme) => ({ height: theme.spacing(8) })}
                >
                  <Text variant="body1" sx={{ color: (theme) => theme.palette.text.tertiary }}>
                    Instant Payout fee
                  </Text>
                  <Text semibold variant="body1">
                    -{formatDollarsAsUsd(effectivePayoutFee)}
                  </Text>
                </Stack>
              )}

              {payoutFee > 0 && isAfterAnotherShift && (
                <>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={(theme) => ({ height: theme.spacing(8) })}
                  >
                    <Text variant="body1" sx={{ color: (theme) => theme.palette.text.tertiary }}>
                      Instant Payout fee
                    </Text>
                    <Stack direction="row" gap={1}>
                      <Text
                        variant="body1"
                        sx={{
                          textDecoration: "line-through",
                          color: (theme) => theme.palette.text.tertiary,
                        }}
                      >
                        -{formatDollarsAsUsd(payoutFee)}
                      </Text>
                      <Text semibold variant="body1">
                        -{formatDollarsAsUsd(0)}
                      </Text>
                    </Stack>
                  </Stack>
                  <PromotionBanner
                    icon="dollar-with-circle"
                    title="It looks like you just finished a double."
                    subtitle="The Instant Payout fee is on us!"
                    borderVariant="gradient"
                    backgroundColor={(theme) => theme.palette.background?.tertiary ?? ""}
                    borderColor={(theme) => theme.palette.onboarding?.inProgress.gradient ?? ""}
                  />
                </>
              )}

              <Divider color="primary" sx={(theme) => ({ marginY: theme.spacing(4) })} />

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Text
                  semibold
                  variant="body1"
                  sx={{
                    color: (theme) =>
                      isMissedPunchRequestEnabledV2 &&
                      isDefined(pendingApprovalAmountInDollars) &&
                      pendingApprovalAmountInDollars > 0
                        ? theme.palette.text.primary
                        : theme.palette.text.tertiary,
                  }}
                >
                  Net payout
                </Text>
                <Text
                  semibold
                  variant="h4"
                  sx={{
                    color: (theme) => theme.palette.text.primary,
                  }}
                >
                  {formatDollarsAsUsd(netEarnings)}
                </Text>
              </Stack>

              {isDefined(pendingApprovalAmountInDollars) && pendingApprovalAmountInDollars > 0 && (
                <>
                  <Divider color="primary" sx={(theme) => ({ marginY: theme.spacing(4) })} />
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Stack>
                      <Text
                        semibold
                        variant="body1"
                        sx={{ color: (theme) => theme.palette.intent?.warning?.text }}
                      >
                        Pending approval
                      </Text>
                      <Text variant="body2" sx={{ color: (theme) => theme.palette.text.tertiary }}>
                        Not included in this payout
                      </Text>
                    </Stack>
                    <Text
                      semibold
                      variant="body1"
                      sx={{ color: (theme) => theme.palette.intent?.warning?.text }}
                    >
                      {formatDollarsAsUsd(pendingApprovalAmountInDollars)}
                    </Text>
                  </Stack>
                </>
              )}
            </Stack>
          )}

          {displayHourlyRate && (
            <Text
              variant="body1"
              sx={{ color: (theme) => theme.palette.text.tertiary, textAlign: "center" }}
            >
              Please visit the Earnings page to view your actual earnings.
            </Text>
          )}
        </Stack>
      </DialogContent>

      <DialogFooter orientation="vertical">
        <LoadingButton
          fullWidth
          variant="contained"
          size="large"
          isLoading={continueButtonIsLoading}
          onClick={() => {
            logEvent(APP_V2_USER_EVENTS.EARNINGS_BOTTOM_SHEET_CONTINUE_CLICKED, {
              shiftId,
              grossEarnings,
              netEarnings: isDefined(grossEarnings) ? netEarnings : undefined,
              hourlyRate: displayHourlyRate ? hourlyRate : undefined,
              isPayoutInstant,
            });
            onContinue();
          }}
        >
          Continue
        </LoadingButton>
      </DialogFooter>
    </FullScreenDialog>
  );
}
/* eslint-enable max-lines */
